import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import _findIndex from "lodash/findIndex";

import { fetchTemplate, parseApiError, ParsedApiError } from "../../lib/api/proposal-api";
import { RootState } from "../../rootReducer";
import type { ProposalSlideDefinition } from "../../types";
import { EnerfloProposalTemplate } from "../../types";

interface TemplateState {
  data: EnerfloProposalTemplate;
  isLoading: boolean;
  error?: {
    statusCode: number;
    message: string;
  };
}

const initialState = {
  isLoading: false,
} as TemplateState;

type ThunkApi<RejectValue = never> = { state: RootState; rejectValue: RejectValue };

export const fetchProposalTemplate = createAsyncThunk<EnerfloProposalTemplate, string, ThunkApi<ParsedApiError>>(
  "template/fetch",
  async (reportHash: string, thunkAPI) => {
    try {
      const response = await fetchTemplate(reportHash, {
        params: {
          type: thunkAPI.getState().proposal.demoType,
        },
      });
      return response.data;
    } catch (e: any) {
      return thunkAPI.rejectWithValue(parseApiError(e));
    }
  }
);

const templateSlice = createSlice({
  name: "template",
  initialState,
  reducers: {
    replaceSlideDeck(state, action: PayloadAction<ProposalSlideDefinition>) {
      state.data.slides = [action.payload];
    },
    updateSlideConfig(state, action: PayloadAction<ProposalSlideDefinition>) {
      const slideIndex = _findIndex(state.data.slides, slide => slide.id === action.payload.id);

      if (slideIndex > -1) {
        state.data.slides[slideIndex].config = action.payload.config;
      } else {
        state.data.slides.push({ id: action.payload.id!, config: action.payload.config! });
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProposalTemplate.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(fetchProposalTemplate.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
        state.error = undefined;
      })
      .addCase(fetchProposalTemplate.rejected, (state, action) => {
        state.isLoading = false;

        if (action.meta.rejectedWithValue) {
          state.error = action.payload as any;
        }
      });
  },
});

export const { updateSlideConfig, replaceSlideDeck } = templateSlice.actions;

export default templateSlice;
